import type { Entitlements } from '@ims/data'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  TD,
  TH,
  Table,
  cx,
  useToast,
  SkeletonForm,
  SkeletonTable,
} from '@ims/ui'
import { CheckCircle2, Clock, Mail, Phone, XCircle } from '@ims/ui/icons'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import { errorMessage, useLive, useOnline, useSettings } from '../hooks.ts'
import { createApi } from '../lib/api.ts'
import { dateLabel, dateTime, money } from '../lib/format.ts'
import { SUPPORT_EMAIL, SUPPORT_PHONE_LABEL, supportMailto, supportTel } from '../lib/support.ts'
import { useSession } from '../session.tsx'
import { useCan, useStore, useWorkspace } from '../workspace.tsx'

// Settings → Subscription: current plan and usage, upgrade/renew through
// Khalti or eSewa (prices always come from the server), payment history.
// Prices are for a month, 6 months (1 month free) or a year (2 months free), VAT
// included; an offer and this business's kept price come from
// subscription_prices (docs/plans/pricing-periods.md).

const LIMIT_LABELS: Record<string, string> = {
  max_users: 'Team members',
  max_counters: 'Billing counters (devices)',
  max_locations: 'Locations',
  max_products: 'Products',
  max_tables: 'Tables',
  max_invoices_per_month: 'Invoices per month',
}

type Period = 'month' | 'half_year' | 'year'

const PERIODS: { value: Period; label: string; free?: string; per: string }[] = [
  { value: 'month', label: 'Monthly', per: 'month' },
  { value: 'half_year', label: '6 months', free: '1 month free', per: '6 months' },
  { value: 'year', label: 'Yearly', free: '2 months free', per: 'year' },
]
const periodWords = (p: Period) => PERIODS.find((x) => x.value === p)!

interface Offer {
  id: string
  label: string
  period: Period
  price_paisa: number
  ends_on: string
  keep_at_renewal: boolean
  max_raise_bp: number
}

interface Plan {
  id: string
  name: string
  limits: Record<string, number | null>
  /** The normal price for each period. */
  list: Record<Period, number>
  /** What this business pays: its kept price, an offer running today, or the list price. */
  price: Record<Period, number>
  offers: Offer[]
}

/** A price this business keeps at renewal (paid at an offer that keeps it). */
interface Kept {
  plan_id: string
  price_paisa: number
  next_price_paisa: number | null
  max_raise_bp: number
}

const percent = (bp: number) => `${(bp / 100).toLocaleString('en-IN')}%`

interface PaymentRow {
  id: string
  plan_id: string
  period: string
  amount_paisa: number
  provider: string
  status: string
  created_at: string
}

const RETURN_MESSAGES: Record<string, { tone: 'success' | 'warning' | 'danger'; text: string }> = {
  success: { tone: 'success', text: 'Payment received — your subscription is active.' },
  pending: {
    tone: 'warning',
    text: 'Payment not confirmed yet. It will be checked again within the hour.',
  },
  cancelled: { tone: 'warning', text: 'Payment cancelled. Nothing was charged.' },
  failed: {
    tone: 'danger',
    text: 'The payment could not be verified. If you were charged, contact support with the time of payment.',
  },
}

/** Sends the browser to eSewa with a signed form (eSewa only accepts a POST). */
function postForm(action: string, fields: Record<string, string>) {
  const form = document.createElement('form')
  form.method = 'POST'
  form.action = action
  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value
    form.appendChild(input)
  }
  document.body.appendChild(form)
  form.submit()
}

export function SubscriptionSettings() {
  const settings = useSettings()
  const { engine } = useStore()
  const { platform } = useSession()
  const { current } = useWorkspace()
  const canPay = useCan('admin')
  const online = useOnline()
  const toast = useToast()
  const [params, setParams] = useSearchParams()
  const [ent, setEnt] = useState<Entitlements | null | undefined>(undefined)
  const [plans, setPlans] = useState<Plan[] | null>(null)
  const [kept, setKept] = useState<Kept | null>(null)
  const [providers, setProviders] = useState<{ khalti: boolean; esewa: boolean } | null>(null)
  const [payments, setPayments] = useState<PaymentRow[]>([])
  // A year is the best price, so it is where the choice starts.
  const [period, setPeriod] = useState<Period>('year')
  const [busy, setBusy] = useState<string | null>(null)
  const returned = params.get('billing')
  const api = createApi(platform.apiUrl, platform.supabase)
  const tenantId = current!.tenant_id

  const usage = useLive(
    async (r) => ({
      max_products: (await r.list('products', { limit: 1 })).total,
      max_locations: (await r.list('locations', { limit: 1 })).total,
      max_tables: (await r.list('dining_tables', { limit: 1 })).total,
      max_counters: (
        await r.list('billing_counters', { where: { kind: 'device', is_active: true }, limit: 1 })
      ).total,
    }),
    [],
    ['products', 'locations', 'billing_counters', 'dining_tables'],
  ).data
  // Tables only matter in restaurant mode.
  const limitLabels = Object.entries(LIMIT_LABELS).filter(
    ([key]) => key !== 'max_tables' || settings?.restaurant,
  )

  // Back from Khalti/eSewa: sync now so the new plan applies right away.
  useEffect(() => {
    if (!returned) return
    void engine.syncNow()
  }, [returned, engine])

  useEffect(() => {
    let active = true
    void engine.cachedEntitlements().then((e) => active && setEnt(e))
    const unsubscribe = engine.subscribe((state) => {
      if (state.phase === 'idle') void engine.cachedEntitlements().then((e) => active && setEnt(e))
    })
    return () => {
      active = false
      unsubscribe()
    }
  }, [engine])

  useEffect(() => {
    if (!online) return
    let active = true
    void platform.supabase
      .rpc('subscription_prices', { p_tenant_id: tenantId })
      .then(({ data }) => {
        if (!active) return
        const prices = data as { plans: Plan[]; kept: Kept | null } | null
        setPlans(prices?.plans ?? [])
        setKept(prices?.kept ?? null)
      })
    void api.billingProviders().then(
      (p) => active && setProviders(p),
      () => active && setProviders({ khalti: false, esewa: false }),
    )
    if (canPay) {
      void platform.supabase
        .from('billing_payments')
        .select('id, plan_id, period, amount_paisa, provider, status, created_at')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false })
        .limit(20)
        .then(({ data }) => active && setPayments((data ?? []) as PaymentRow[]))
    }
    return () => {
      active = false
    }
    // api is rebuilt each render; its inputs are platform + tenant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online, platform, tenantId, canPay, returned])

  const pay = async (planId: string, provider: 'khalti' | 'esewa') => {
    setBusy(`${planId}:${provider}`)
    try {
      const result = await api.checkout(tenantId, planId, period, provider)
      if (result.kind === 'redirect') window.location.assign(result.url)
      else postForm(result.action, result.fields)
    } catch (error) {
      // Stays until it is read: nothing was paid, and the next step is theirs.
      toast.error('The payment could not be started', {
        detail: errorMessage(error),
        sticky: true,
      })
      setBusy(null)
    }
  }

  if (ent === undefined) return <SkeletonForm cards={2} fields={2} />
  const message = returned ? RETURN_MESSAGES[returned] : null
  const businessName = current?.tenant.name ?? ''
  const onlinePayment = !!providers && (providers.khalti || providers.esewa)
  /** An email asking us to activate a plan (while online payment isn't live). */
  const requestMail = (plan?: Plan) =>
    supportMailto(
      plan
        ? `Subscribe: ${plan.name} (${periodWords(period).label.toLowerCase()})`
        : 'Subscription',
      [
        `Business: ${businessName}`,
        `Business ID: ${tenantId}`,
        plan
          ? `Plan: ${plan.name}, ${periodWords(period).label.toLowerCase()} (${money(plan.price[period])})`
          : '',
        '',
        'Please activate this plan. My phone number is: ',
      ]
        .filter((line, i) => line !== '' || i > 2)
        .join('\n'),
    )

  return (
    <div className="space-y-4">
      {message && (
        <div
          className={cx(
            'flex items-start justify-between gap-3 rounded-xl p-3 text-sm',
            message.tone === 'success'
              ? 'bg-success-light text-success'
              : message.tone === 'warning'
                ? 'bg-warning-light text-warning'
                : 'bg-danger-light text-danger',
          )}
        >
          <span>{message.text}</span>
          <button
            type="button"
            className="text-xs underline"
            onClick={() => setParams({ tab: 'subscription' }, { replace: true })}
          >
            Dismiss
          </button>
        </div>
      )}

      <Card>
        {!ent ? (
          <EmptyState title="Sync once while online to load your plan" />
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-lg font-semibold capitalize">{ent.plan_id} plan</h2>
              <Badge tone={ent.can_write ? 'success' : 'danger'}>{ent.status}</Badge>
            </div>
            <p className="mt-1 text-sm text-muted">
              {ent.current_period_end &&
                `Current period ends ${dateLabel(ent.current_period_end, settings)}. `}
              {ent.grace_until &&
                `Billing keeps working offline until ${dateLabel(ent.grace_until, settings)}.`}
            </p>
            {kept && (
              <div
                role="note"
                className="mt-3 max-w-3xl rounded-xl bg-primary-light px-4 py-3 text-sm text-ink"
              >
                <b>
                  Your price is kept: {money(kept.price_paisa)} a year
                  {plans?.find((p) => p.id === kept.plan_id)
                    ? ` for ${plans.find((p) => p.id === kept.plan_id)!.name}`
                    : ''}
                  .
                </b>{' '}
                {kept.next_price_paisa
                  ? `From your next renewal it is ${money(kept.next_price_paisa)} a year, for the new features added.`
                  : `Renewing for a year stays at this price. If big new features arrive, a renewal may rise by at most ${percent(kept.max_raise_bp)}, and we tell you a month before.`}
              </div>
            )}
            <table className="mt-4 w-full max-w-md text-sm">
              <tbody>
                {limitLabels.map(([key, label]) => (
                  <tr key={key} className="border-b border-border">
                    <td className="py-2 text-muted">{label}</td>
                    <td className="tabular py-2 text-right">
                      {usage && key in usage ? `${usage[key as keyof typeof usage]} / ` : ''}
                      {ent.limits[key] ?? 'Unlimited'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </Card>

      <Card>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-semibold">Upgrade or renew</h2>
          <div
            className="grid grid-cols-3 gap-1 rounded-xl bg-soft p-1 text-sm"
            role="group"
            aria-label="Billing period"
          >
            {PERIODS.map((p) => (
              <button
                key={p.value}
                type="button"
                aria-pressed={period === p.value}
                onClick={() => setPeriod(p.value)}
                className={cx(
                  'rounded-lg px-3 py-1 font-medium whitespace-nowrap',
                  period === p.value ? 'bg-surface shadow-card' : 'text-muted',
                )}
              >
                {p.label}
                {p.free && <span className="max-sm:hidden"> ({p.free})</span>}
              </button>
            ))}
          </div>
        </div>
        {!online ? (
          <p className="text-sm text-muted">Connect to the internet to pay.</p>
        ) : !plans || !providers ? (
          <SkeletonTable rows={3} cols={3} label="Loading plans" />
        ) : (
          <>
            {!canPay && (
              <p className="mb-3 text-sm text-muted">
                Only owners and admins can pay for the subscription.
              </p>
            )}
            {!onlinePayment && (
              <div className="mb-4 rounded-xl bg-primary-light p-4 text-sm">
                <p className="font-medium text-ink">Online payment is coming soon.</p>
                <p className="mt-1 text-muted">
                  To upgrade or renew now, email or call us and we’ll activate your plan.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <a
                    href={requestMail()}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-3 py-1.5 font-medium text-on-fill hover:bg-primary-dark"
                  >
                    <Mail className="size-4" /> {SUPPORT_EMAIL}
                  </a>
                  <a
                    href={supportTel}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-surface px-3 py-1.5 font-medium text-ink hover:bg-soft"
                  >
                    <Phone className="size-4" /> {SUPPORT_PHONE_LABEL}
                  </a>
                </div>
              </div>
            )}
            <div className="grid gap-4 md:grid-cols-3">
              {plans.map((plan) => {
                const price = plan.price[period]
                const list = plan.list[period]
                const offer = plan.offers.find((o) => o.period === period)
                const keptHere = period === 'year' && kept?.plan_id === plan.id
                const isCurrent = ent?.plan_id === plan.id
                return (
                  <div
                    key={plan.id}
                    className={cx(
                      'rounded-xl border p-4',
                      isCurrent ? 'border-primary' : 'border-border',
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold">{plan.name}</h3>
                      {isCurrent && <Badge tone="primary">Current</Badge>}
                    </div>
                    {price < list && (
                      <p className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                        {offer && !keptHere && <Badge tone="success">{offer.label}</Badge>}
                        <s className="tabular text-muted">
                          {money(list)}
                          {keptHere ? ' for new customers' : ''}
                        </s>
                      </p>
                    )}
                    <p className="tabular mt-1 text-2xl font-bold">
                      {money(price)}
                      <span className="text-sm font-normal text-muted">
                        /{periodWords(period).per}
                      </span>
                    </p>
                    <p className="text-xs text-muted">
                      {keptHere
                        ? 'Your kept price'
                        : offer && price < list
                          ? `Until ${dateLabel(offer.ends_on, settings)}${offer.keep_at_renewal ? ', and kept when you renew' : ''}`
                          : periodWords(period).free
                            ? `${periodWords(period).free}, VAT included`
                            : 'VAT included'}
                    </p>
                    <ul className="mt-3 space-y-1 text-sm text-muted">
                      {limitLabels.map(([key, label]) => (
                        <li key={key}>
                          {plan.limits[key] ?? 'Unlimited'} {label.toLowerCase()}
                        </li>
                      ))}
                    </ul>
                    {canPay && !onlinePayment && (
                      <a
                        href={requestMail(plan)}
                        className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-border bg-surface px-3 py-1.5 text-sm font-medium text-ink hover:bg-soft"
                      >
                        <Mail className="size-4" /> Request {plan.name}
                      </a>
                    )}
                    {canPay && onlinePayment && (
                      <div className="mt-4 flex flex-col gap-2">
                        {providers.khalti && (
                          <Button
                            size="sm"
                            loading={busy === `${plan.id}:khalti`}
                            disabled={busy !== null}
                            onClick={() => void pay(plan.id, 'khalti')}
                          >
                            Pay with Khalti
                          </Button>
                        )}
                        {providers.esewa && (
                          <Button
                            size="sm"
                            variant="secondary"
                            loading={busy === `${plan.id}:esewa`}
                            disabled={busy !== null}
                            onClick={() => void pay(plan.id, 'esewa')}
                          >
                            Pay with eSewa
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
              <div className="rounded-xl border border-border p-4">
                <h3 className="font-semibold">Custom</h3>
                <p className="mt-2 text-2xl font-bold">Let’s talk</p>
                <p className="text-xs text-muted">A price for what you need</p>
                <ul className="mt-3 space-y-1 text-sm text-muted">
                  <li>More counters and branches</li>
                  <li>Unlimited products and invoices</li>
                  <li>Help moving your data in</li>
                </ul>
                <div className="mt-4 flex flex-col gap-2">
                  <a
                    href={supportMailto(
                      'Custom plan',
                      [
                        `Business: ${businessName}`,
                        `Business ID: ${tenantId}`,
                        '',
                        'We would like a Custom plan. My phone number is: ',
                      ].join('\n'),
                    )}
                    className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-border bg-surface px-3 py-1.5 text-sm font-medium text-ink hover:bg-soft"
                  >
                    <Mail className="size-4" /> Email us
                  </a>
                  <a
                    href={supportTel}
                    className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-border bg-surface px-3 py-1.5 text-sm font-medium text-ink hover:bg-soft"
                  >
                    <Phone className="size-4" /> {SUPPORT_PHONE_LABEL}
                  </a>
                </div>
              </div>
            </div>
            <p className="mt-3 text-xs text-muted">
              Prices include VAT. Paying extends your subscription from the end of the current
              period, so no days are lost.
            </p>
            <p className="mt-3 border-t border-border pt-3 text-xs text-muted">
              Questions about your plan or a payment? Email{' '}
              <a href={requestMail()} className="font-medium text-primary hover:underline">
                {SUPPORT_EMAIL}
              </a>{' '}
              or call{' '}
              <a href={supportTel} className="font-medium text-primary hover:underline">
                {SUPPORT_PHONE_LABEL}
              </a>
              .
            </p>
          </>
        )}
      </Card>

      {canPay && payments.length > 0 && (
        <Card className="p-0">
          <h2 className="border-b border-border px-5 py-3 font-semibold">Payment history</h2>
          <Table>
            <thead>
              <tr>
                <TH>When</TH>
                <TH>Plan</TH>
                <TH>Method</TH>
                <TH>Status</TH>
                <TH align="right">Amount</TH>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id}>
                  <TD className="text-muted">{dateTime(p.created_at, settings)}</TD>
                  <TD className="capitalize">
                    {p.plan_id} / {p.period === 'half_year' ? '6 months' : p.period}
                  </TD>
                  <TD className="capitalize">{p.provider}</TD>
                  <TD>
                    {p.status === 'completed' ? (
                      <span className="inline-flex items-center gap-1 text-success">
                        <CheckCircle2 className="size-4" /> Paid
                      </span>
                    ) : p.status === 'pending' ? (
                      <span className="inline-flex items-center gap-1 text-warning">
                        <Clock className="size-4" /> Pending
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-muted">
                        <XCircle className="size-4" /> {p.status}
                      </span>
                    )}
                  </TD>
                  <TD align="right">{money(p.amount_paisa)}</TD>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
    </div>
  )
}
